import { useDeferredValue, useState } from 'react'
import { ImageOff, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { PoiIcon } from '@/lib/poi-icon'
import {
  CATEGORY_LABELS,
  ICON_CATALOG,
  ICON_CATEGORIES,
  type IconCategory,
} from '@/lib/icon-catalog'
import { cn } from '@/lib/utils'

interface IconPickerProps {
  value: string | null
  onChange: (name: string) => void
}

function matchesSearch(icon: { name: string; keywords?: string[] }, term: string): boolean {
  const q = term.toLowerCase()
  if (icon.name.toLowerCase().includes(q)) return true
  // human label: "ShoppingBag" → "shopping bag"
  const label = icon.name.replace(/([A-Z])/g, ' $1').trim().toLowerCase()
  if (label.includes(q)) return true
  return icon.keywords?.some(k => k.includes(q)) ?? false
}

export function IconPicker({ value, onChange }: IconPickerProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const deferredSearch = useDeferredValue(search)

  function pick(name: string) {
    onChange(name)
    setOpen(false)
    setSearch('')
  }

  function handleOpenChange(isOpen: boolean) {
    setOpen(isOpen)
    if (!isOpen) setSearch('')
  }

  const isSearching = deferredSearch.trim().length > 0
  const searchResults = isSearching
    ? ICON_CATALOG.filter(icon => matchesSearch(icon, deferredSearch))
    : []

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          type="button"
          className="size-9 shrink-0"
          aria-label={value ? `Icon: ${value}` : 'Pick an icon'}
          title={value ? value.replace(/([A-Z])/g, ' $1').trim() : 'Pick an icon'}
        >
          {value
            ? <PoiIcon name={value} size={18} />
            : <ImageOff className="size-4 text-muted-foreground" />}
        </Button>
      </PopoverTrigger>

      {/* onWheel/onTouchMove stop propagation: prevents Sheet scroll-lock from swallowing events */}
      <PopoverContent
        className="w-72 p-0"
        align="start"
        onWheel={e => e.stopPropagation()}
        onTouchMove={e => e.stopPropagation()}
      >
        {/* Sticky search header */}
        <div className="border-b p-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 size-3.5 text-muted-foreground" />
            <Input
              placeholder="Search icons..."
              aria-label="Search icons"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 pl-8 text-sm"
              autoFocus
            />
          </div>
        </div>

        {/* Icon grid — fixed height so overflow-y-auto always creates a scroll container */}
        <div className="h-72 overflow-y-auto overscroll-contain">
          {isSearching ? (
            <div className="p-2">
              {searchResults.length > 0 ? (
                <div className="grid grid-cols-7 gap-0.5">
                  {searchResults.map(({ name, Icon }) => (
                    <IconButton
                      key={name}
                      name={name}
                      Icon={Icon}
                      selected={value === name}
                      onPick={pick}
                    />
                  ))}
                </div>
              ) : (
                <p className="py-6 text-center text-xs text-muted-foreground">
                  No icons match &ldquo;{deferredSearch}&rdquo;
                </p>
              )}
            </div>
          ) : (
            <div className="p-2 space-y-3">
              {ICON_CATEGORIES.map(cat => {
                const icons = ICON_CATALOG.filter(i => i.category === cat)
                return (
                  <div key={cat}>
                    <p className="mb-1 px-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {CATEGORY_LABELS[cat as IconCategory]}
                    </p>
                    <div className="grid grid-cols-7 gap-0.5">
                      {icons.map(({ name, Icon }) => (
                        <IconButton
                          key={name}
                          name={name}
                          Icon={Icon}
                          selected={value === name}
                          onPick={pick}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {value && (
          <>
            <Separator />
            <div className="p-1">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className="w-full justify-start gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => { onChange(''); setOpen(false) }}
              >
                <X className="size-3" />
                Clear icon
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}

interface IconButtonProps {
  name: string
  Icon: React.ComponentType<{ size?: number; 'aria-hidden'?: boolean | 'true' | 'false' }>
  selected: boolean
  onPick: (name: string) => void
}

function IconButton({ name, Icon, selected, onPick }: IconButtonProps) {
  const label = name.replace(/([A-Z])/g, ' $1').trim()
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={() => onPick(name)}
      className={cn(
        'flex items-center justify-center rounded-md p-2 transition-colors',
        'hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        selected && 'bg-accent ring-1 ring-ring',
      )}
    >
      <Icon size={16} aria-hidden="true" />
    </button>
  )
}
